import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import Navbar from "../../Navbar";
import {
  anularOrdenPago,
  descargarDocumentoOrdenPago,
  getOrdenPagoById,
} from "./ApiServices";
import CertificadoRetencionCard from "./CertificadoRetencionCard";
import ModalAnularOrdenPago from "./ModalAnularOrdenPago";
import ResumenSeleccion from "./ResumenSeleccion";
import VistaOrdenPagoDocumento from "./VistaOrdenPagoDocumento";
import {
  colorEstadoLote,
  colorEstadoOp,
  esOpAnulable,
  esOpPendienteAprobacion,
  etiquetaEstadoLote,
  etiquetaEstadoOp,
  formatMoneda,
  guardarBlobComoArchivo,
  listarCertificadosRetencion,
  mapDetalleOrdenPagoResponse,
  mensajeErrorAnularOp,
  obtenerLoteDeOrdenPago,
  obtenerResumenPreview,
} from "./utils";

export default function DetalleOrdenPago() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [respuesta, setRespuesta] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [descargaActiva, setDescargaActiva] = useState(null);
  const [errorDescarga, setErrorDescarga] = useState(null);
  const [modalAnularOpen, setModalAnularOpen] = useState(false);
  const [anulando, setAnulando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getOrdenPagoById(id);
      setDetalle(mapDetalleOrdenPagoResponse(data));
      setRespuesta(data);
    } catch (err) {
      setError(err.message || "Error al cargar la orden de pago");
      setDetalle(null);
      setRespuesta(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const vista = useMemo(() => detalle ?? {}, [detalle]);
  const archivoInterbanking = vista.ordenPago?.archivo_interbanking;
  // La OP puede formar parte de un lote (revisión y aprobación conjunta).
  const lote = useMemo(
    () =>
      obtenerLoteDeOrdenPago(respuesta?.orden_pago) ??
      obtenerLoteDeOrdenPago(respuesta) ??
      obtenerLoteDeOrdenPago(vista.ordenPago),
    [respuesta, vista.ordenPago]
  );
  const certificados = useMemo(
    () =>
      listarCertificadosRetencion(vista.preview).map((cert) => ({
        ...cert,
        pdfEnviadoPorMail: Boolean(vista.notificacionProveedor?.mail_enviado),
      })),
    [vista.preview, vista.notificacionProveedor]
  );
  const certificadosConRetencion = certificados.filter(
    (c) => c.aplica && Number(c.importe) > 0
  );

  const esTransferenciaBanco = Boolean(
    vista.medioPago?.transferencia?.activo ||
      (Array.isArray(vista.mediosPagoApi) &&
        vista.mediosPagoApi.some(
          (m) =>
            m?.tipo === "transferencia" ||
            (m?.tipo === "banco" && m?.tipo_bancario === "transferencia")
        )) ||
      (vista.medioPago?.categoria === "banco" &&
        vista.medioPago?.tipoBanco === "transferencia")
  );
  const tieneInterbanking =
    Boolean(archivoInterbanking) || esTransferenciaBanco;
  const tieneRetencionesDescargables = certificadosConRetencion.some(
    (c) => c.idRetencion
  );

  const descargando = (clave) => descargaActiva === clave;

  const descargarDocumento = useCallback(
    async (tipo, { idRetencion, clave } = {}) => {
      const key = clave ?? (idRetencion ? `retencion-${idRetencion}` : tipo);
      setDescargaActiva(key);
      setErrorDescarga(null);

      try {
        const { blob, filename } = await descargarDocumentoOrdenPago(id, {
          tipo,
          idRetencion,
        });
        guardarBlobComoArchivo(blob, filename);
      } catch (err) {
        setErrorDescarga(err.message || "Error al descargar el documento");
      } finally {
        setDescargaActiva(null);
      }
    },
    [id]
  );

  const descargarRetencionPdf = useCallback(
    (idRetencion) =>
      descargarDocumento("retencion_pdf", {
        idRetencion,
        clave: `retencion-${idRetencion}`,
      }),
    [descargarDocumento]
  );

  const copiarInterbanking = async () => {
    if (!archivoInterbanking) return;
    try {
      await navigator.clipboard.writeText(archivoInterbanking);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };

  const descargarInterbanking = () => {
    descargarDocumento("interbanking", { clave: "interbanking" });
  };

  const pendienteAprobacion = esOpPendienteAprobacion(vista.ordenPago?.estado);
  const enLote = Boolean(lote?.id);
  const puedeEditarAprobar = pendienteAprobacion && !enLote;
  const puedeAnular = esOpAnulable(vista.ordenPago?.estado) && !enLote;

  const handleAnular = async (motivo) => {
    setAnulando(true);
    try {
      const data = await anularOrdenPago(id, {
        user: localStorage.getItem("user"),
        motivo,
      });
      setModalAnularOpen(false);

      if (data?.ya_anulada) {
        await swal({
          title: "Ya estaba anulada",
          text: data?.msg || "La orden de pago ya se encontraba anulada.",
          icon: "info",
          closeOnClickOutside: false,
        });
      } else {
        await swal({
          title: "Orden anulada",
          text:
            data?.msg ||
            "Se liberaron las facturas y quedaron disponibles otra vez.",
          icon: "success",
          closeOnClickOutside: false,
        });
      }

      // Refresca badge/estado (Anulada) y oculta acciones operativas.
      await cargar();
    } catch (err) {
      swal({
        title: "No se pudo anular",
        text: mensajeErrorAnularOp(err),
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setAnulando(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/ordenes_pago_proveedores")}
            >
              Volver al listado
            </Button>
            {vista.ordenPago?.estado ? (
              <Chip
                label={etiquetaEstadoOp(
                  vista.ordenPago.estado,
                  vista.ordenPago.estado_label
                )}
                size="small"
                color={colorEstadoOp(vista.ordenPago.estado)}
                variant="outlined"
              />
            ) : null}
            {lote ? (
              <Tooltip title="Ver el lote completo" arrow>
                <Chip
                  label={`Lote ${lote.nro_lote ?? lote.id}${
                    lote.estado
                      ? ` · ${etiquetaEstadoLote(lote.estado, lote.estado_label)}`
                      : ""
                  }`}
                  size="small"
                  color={colorEstadoLote(lote.estado)}
                  variant="outlined"
                  clickable
                  onClick={() => navigate(`/lotes_orden_pago/${lote.id}`)}
                />
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight={700}>
            Detalle de orden de pago
            {vista.ordenPago?.nro_op ? ` · ${vista.ordenPago.nro_op}` : ""}
          </Typography>
          {!loading && detalle ? (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {enLote ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/lotes_orden_pago/${lote.id}`)}
                >
                  Ir al lote
                </Button>
              ) : null}
              {puedeEditarAprobar ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() =>
                    navigate(`/ordenes_pago_proveedores/${id}/editar`)
                  }
                >
                  Editar y aprobar
                </Button>
              ) : null}
              {puedeAnular ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<BlockIcon />}
                  onClick={() => setModalAnularOpen(true)}
                  disabled={anulando}
                >
                  Anular
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {errorDescarga ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorDescarga(null)}>
            {errorDescarga}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : detalle ? (
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} lg={8}>
              <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Fecha OP
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {vista.ordenPago?.fecha
                        ? moment(vista.ordenPago.fecha).format("DD/MM/YYYY")
                        : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Usuario
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {vista.ordenPago?.user || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Alta
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {vista.ordenPago?.created_at
                        ? moment(vista.ordenPago.created_at).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total neto
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatMoneda(obtenerResumenPreview(vista.preview)?.total_a_pagar)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Concepto
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                      <Chip
                        size="small"
                        color={
                          String(vista.ordenPago?.concepto || "").toLowerCase() ===
                          "anticipo"
                            ? "info"
                            : "default"
                        }
                        label={
                          String(vista.ordenPago?.concepto || "").toLowerCase() ===
                          "anticipo"
                            ? "Adelanto"
                            : "Pago"
                        }
                      />
                    </Stack>
                  </Grid>
                  {vista.ordenPago?.motivo ? (
                    <Grid item xs={12} sm={6} md={9}>
                      <Typography variant="caption" color="text.secondary">
                        Motivo
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {vista.ordenPago.motivo}
                      </Typography>
                    </Grid>
                  ) : null}
                  {vista.anticipo ? (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 0.5 }}>
                        Adelanto #{vista.anticipo.id}
                        {vista.anticipo.monto != null
                          ? ` · Monto ${formatMoneda(vista.anticipo.monto)}`
                          : ""}
                        {vista.anticipo.saldo_sin_aplicar != null
                          ? ` · Saldo sin aplicar ${formatMoneda(
                              vista.anticipo.saldo_sin_aplicar
                            )}`
                          : ""}
                        . Queda como saldo a favor del proveedor.
                      </Alert>
                    </Grid>
                  ) : null}
                  {Array.isArray(vista.adelantosAplicados) &&
                  vista.adelantosAplicados.length > 0 ? (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={false} sx={{ mt: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          Adelantos aplicados en esta OP
                          {pendienteAprobacion
                            ? " · podés ajustar los importes antes de aprobar"
                            : ""}
                        </Typography>
                        <Stack spacing={1.25}>
                          {vista.adelantosAplicados.map((ad) => {
                            const key =
                              ad.id_anticipo != null
                                ? `ad-${ad.id_anticipo}`
                                : ad.nro_comprobante;
                            return (
                              <Box
                                key={key}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 1,
                                  bgcolor: "rgba(2, 136, 209, 0.06)",
                                  border: "1px solid rgba(2, 136, 209, 0.2)",
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  Adelanto {ad.nro_comprobante || `#${ad.id_anticipo}`}
                                  {ad.descripcion_breve
                                    ? ` · ${ad.descripcion_breve}`
                                    : ""}
                                </Typography>
                                <Grid container spacing={1} sx={{ mt: 0.25 }}>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">
                                      Monto anticipo
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatMoneda(
                                        ad.monto_anticipo ?? ad.total ?? 0
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">
                                      Aplicado en esta OP
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatMoneda(
                                        ad.importe_aplicado ??
                                          Math.abs(Number(ad.importe_a_pagar) || 0)
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">
                                      Tope editable
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatMoneda(
                                        ad.importe_maximo ??
                                          ad.saldo_disponible ??
                                          0
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">
                                      Saldo sin aplicar
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {ad.saldo_sin_aplicar != null
                                        ? formatMoneda(ad.saldo_sin_aplicar)
                                        : "—"}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Alert>
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>

              <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Descargar documentos
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  OP, Interbanking, retenciones o paquete completo
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      descargando("completo") ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <FolderZipOutlinedIcon />
                      )
                    }
                    disabled={Boolean(descargaActiva)}
                    onClick={() => descargarDocumento("completo", { clave: "completo" })}
                  >
                    Todo (ZIP)
                  </Button>
                  {tieneInterbanking ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        descargando("interbanking") ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      disabled={Boolean(descargaActiva)}
                      onClick={() =>
                        descargarDocumento("interbanking", { clave: "interbanking" })
                      }
                    >
                      Interbanking (TXT)
                    </Button>
                  ) : null}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      descargando("op_pdf") ? (
                        <CircularProgress size={16} />
                      ) : (
                        <PictureAsPdfOutlinedIcon />
                      )
                    }
                    disabled={Boolean(descargaActiva)}
                    onClick={() => descargarDocumento("op_pdf", { clave: "op_pdf" })}
                  >
                    Orden de pago (PDF)
                  </Button>
                  {tieneRetencionesDescargables ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        descargando("retenciones_zip") ? (
                          <CircularProgress size={16} />
                        ) : (
                          <FolderZipOutlinedIcon />
                        )
                      }
                      disabled={Boolean(descargaActiva)}
                      onClick={() =>
                        descargarDocumento("retenciones_zip", {
                          clave: "retenciones_zip",
                        })
                      }
                    >
                      Retenciones (ZIP)
                    </Button>
                  ) : null}
                </Stack>
              </Paper>

              <VistaOrdenPagoDocumento
                preview={vista.preview}
                detalle={vista.detalle}
                comprobantesTabla={vista.comprobantesTabla}
                medioPago={vista.medioPago}
                cuentasOrigenDetalle={vista.cuentasOrigenDetalle}
                nroOp={vista.ordenPago?.nro_op}
                estadoOrden={vista.ordenPago?.estado}
                estadoLabel={vista.ordenPago?.estado_label}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}>
                <AccountBalanceWalletOutlinedIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Certificados de retención
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Documentos persistidos al generar la orden de pago
                  </Typography>
                </Box>
              </Box>

              {certificados.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No hay certificados de retención para esta orden.
                </Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                  {certificados.map((cert) => (
                    <CertificadoRetencionCard
                      key={cert.id}
                      certificado={cert}
                      onDescargarPdf={descargarRetencionPdf}
                      descargandoPdf={descargando(`retencion-${cert.idRetencion}`)}
                    />
                  ))}
                </Box>
              )}

              {certificadosConRetencion.length > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: "block" }}
                >
                  Total retenido en certificados:{" "}
                  <strong>
                    {formatMoneda(
                      certificadosConRetencion.reduce(
                        (s, c) => s + Number(c.importe || 0),
                        0
                      )
                    )}
                  </strong>
                </Typography>
              ) : null}

              {vista.notificacionProveedor ? (
                <Alert
                  severity={
                    vista.notificacionProveedor.mail_enviado ? "success" : "warning"
                  }
                  sx={{ mb: 2 }}
                >
                  {vista.notificacionProveedor.mail_enviado
                    ? `Documentación enviada por mail a ${vista.notificacionProveedor.destinatario || "el proveedor"}.`
                    : `Mail no enviado${
                        vista.notificacionProveedor.motivo_no_envio
                          ? `: ${vista.notificacionProveedor.motivo_no_envio}`
                          : "."
                      }`}
                  {vista.notificacionProveedor.nombre_adjunto ? (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Adjunto: {vista.notificacionProveedor.nombre_adjunto}
                    </Typography>
                  ) : null}
                </Alert>
              ) : null}

              {archivoInterbanking ? (
                <Paper elevation={1} sx={{ p: 2, backgroundColor: "#fff" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Archivo Interbanking
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={copiado ? "Copiado" : "Copiar"}>
                        <IconButton size="small" onClick={copiarInterbanking}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Descargar .txt (servidor)">
                        <span>
                          <IconButton
                            size="small"
                            onClick={descargarInterbanking}
                            disabled={Boolean(descargaActiva)}
                          >
                            {descargando("interbanking") ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={14}
                    value={archivoInterbanking}
                    InputProps={{ readOnly: true }}
                    size="small"
                  />
                </Paper>
              ) : null}
            </Grid>

            <Grid
              item
              xs={12}
              lg={4}
              sx={{
                position: { lg: "sticky" },
                top: { lg: 88 },
                alignSelf: "flex-start",
              }}
            >
              <ResumenSeleccion
                preview={vista.preview}
                previewLoading={false}
                previewError={null}
                formatMoneda={formatMoneda}
                cantidadSeleccionada={vista.comprobantesTabla?.length ?? 0}
                filasSeleccionadas={vista.comprobantesTabla ?? []}
              />
            </Grid>
          </Grid>
        ) : null}
      </Container>

      <ModalAnularOrdenPago
        open={modalAnularOpen}
        onClose={() => {
          if (!anulando) setModalAnularOpen(false);
        }}
        onConfirm={handleAnular}
        nroOp={vista.ordenPago?.nro_op || ""}
        anulando={anulando}
      />
    </>
  );
}
